import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import { assertOk } from '../utils/api';
import {
    Box,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';

export default function UserManagement({ token }) {
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState({
        username: '',
        sector: '',
        password: '',
        initial_saturdays: 0,
        active: true
    });

    const fetchUsers = () => {
        fetch(`${API_URL}/api/users`, {
            headers: { Authorization: 'Bearer ' + token }
        })
            .then(r => r.json())
            .then(setUsers)
            .catch(console.error);
    };
    useEffect(fetchUsers, []);

    const handleEdit = u => {
        setCurrent({
            username: u.username,
            sector: u.sector,
            password: '',
            initial_saturdays: u.initial_saturdays,
            active: u.active !== false
        });
        setOpen(true);
    };
    const handleClose = () => setOpen(false);

    const handleSave = () => {
        fetch(`${API_URL}/api/users/${encodeURIComponent(current.username)}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            body: JSON.stringify({
                sector: current.sector,
                password: current.password || undefined,
                initial_saturdays: Number(current.initial_saturdays),
                active: current.active
            })
        })
            .then(async r => {
                await assertOk(r, 'Erro ao salvar usuário');
                return r.json();
            })
            .then(() => {
                fetchUsers();
                setOpen(false);
            })
            .catch(err => alert(err.message || 'Erro ao salvar usuário'));
    };

    const columns = [
        {
            field: 'username',
            headerName: 'Usuário',
            flex: 1,
            renderCell: params => (
                <span>
                    {params.value}{' '}
                    <em>
                        ({params.row.saturday_count}{' '}
                        {params.row.saturday_count === 1 ? 'sábado' : 'sábados'})
                    </em>
                </span>
            )
        },
        { field: 'sector', headerName: 'Setor', flex: 1 },
        {
            field: 'active',
            headerName: 'Status',
            flex: 0.7,
            renderCell: params => (
                <Chip
                    size="small"
                    label={params.value === false ? 'Inativo' : 'Ativo'}
                    color={params.value === false ? 'default' : 'success'}
                    variant={params.value === false ? 'outlined' : 'filled'}
                />
            )
        },
        {
            field: 'actions',
            headerName: 'Ações',
            flex: 0.5,
            sortable: false,
            renderCell: params => (
                <IconButton onClick={() => handleEdit(params.row)}>
                    <EditIcon />
                </IconButton>
            )
        }
    ];

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Gerenciar Usuários
            </Typography>
            <Box
                sx={{
                    height: { xs: 'calc(100vh - 300px)', sm: 'calc(100vh - 260px)', md: 'calc(100vh - 220px)' },
                    minHeight: 420,
                    width: '100%'
                }}
            >
                <DataGrid
                    rows={users}
                    columns={columns}
                    getRowId={r => r.username}
                    initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                />
            </Box>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Editar Usuário</DialogTitle>
                <DialogContent sx={{ display: 'grid', gap: 2, width: 300, mt: 1 }}>
                    <TextField
                        label="Usuário"
                        value={current.username}
                        disabled
                        fullWidth
                    />
                    <FormControl fullWidth>
                        <InputLabel>Setor</InputLabel>
                        <Select
                            value={current.sector}
                            label="Setor"
                            onChange={e =>
                                setCurrent(p => ({ ...p, sector: e.target.value }))
                            }
                        >
                            <MenuItem value="DEV">DEV</MenuItem>
                            <MenuItem value="TI">TI</MenuItem>
                            <MenuItem value="SAF">SAF</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label="Nova senha"
                        type="password"
                        value={current.password}
                        onChange={e =>
                            setCurrent(p => ({ ...p, password: e.target.value }))
                        }
                        fullWidth
                    />
                    <TextField
                        label="Sábados iniciais"
                        type="number"
                        value={current.initial_saturdays}
                        onChange={e =>
                            setCurrent(p => ({
                                ...p,
                                initial_saturdays: e.target.value
                            }))
                        }
                        helperText="Ajuste manual de sábados anteriores"
                        fullWidth
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={current.active}
                                onChange={e =>
                                    setCurrent(p => ({ ...p, active: e.target.checked }))
                                }
                            />
                        }
                        label="Usuário ativo"
                    />
                    {!current.active && (
                        <Typography variant="caption" color="text.secondary">
                            Usuário inativo não consegue mais fazer login, mas continua
                            aparecendo normalmente nos relatórios já gerados.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
